import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const OrderSummary = ({
  plan,
  billingCycle,
  subtotal,
  tax,
  discount,
  total,
  promoCode,
  promoApplied,
  onPromoCodeChange,
  onApplyPromo,
  onRemovePromo
}) => {
  return (
    <div className="bg-card rounded-xl border border-border p-6 shadow-warm-md">
      <h2 className="text-lg font-heading font-bold text-foreground mb-4">
        Order Summary
      </h2>
      {/* Plan Details */}
      <div className="space-y-3 mb-6">
        <div className="flex justify-between items-start">
          <div>
            <p className="font-medium text-foreground">{plan?.name} Plan</p>
            <p className="text-sm text-muted-foreground">
              {billingCycle === 'monthly' ? 'Monthly' : 'Annual'} subscription
            </p>
          </div>
          <p className="font-semibold text-foreground">${subtotal}</p>
        </div>

        {/* Features List */}
        <div className="bg-muted/30 rounded-lg p-3 space-y-2">
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">
            Included Features
          </p>
          {plan?.features?.slice(0, 5)?.map((feature, index) => (
            <div key={index} className="flex items-start gap-2">
              <Icon name="Check" size={14} className="text-success mt-0.5 flex-shrink-0" />
              <span className="text-sm text-foreground">{feature}</span>
            </div>
          ))}
        </div>
      </div>
      {/* Promo Code */}
      <div className="mb-6">
        <div className="flex gap-2">
          <Input
            placeholder="Enter promo code"
            value={promoCode}
            onChange={(e) => onPromoCodeChange(e?.target?.value)}
            disabled={promoApplied}
            className="flex-1"
          />
          {promoApplied ? (
            <Button
              variant="outline"
              size="default"
              onClick={onRemovePromo}
              iconName="X"
            >
              Remove
            </Button>
          ) : (
            <Button
              variant="outline"
              size="default"
              onClick={onApplyPromo}
              disabled={!promoCode}
            >
              Apply
            </Button>
          )}
        </div>
        {promoApplied && (
          <div className="flex items-center gap-2 mt-2 text-sm text-success">
            <Icon name="CheckCircle" size={14} />
            <span>Promo code applied successfully!</span>
          </div>
        )}
        <p className="text-xs text-muted-foreground mt-2">
          Try: SAVE20 or FIRST10
        </p>
      </div>
      {/* Price Breakdown */}
      <div className="space-y-3 mb-6 pb-6 border-b border-border">
        <div className="flex justify-between items-center text-sm">
          <span className="text-muted-foreground">Subtotal</span>
          <span className="text-foreground font-medium">${subtotal}</span>
        </div>
        {discount > 0 && (
          <div className="flex justify-between items-center text-sm">
            <span className="text-success">Discount</span>
            <span className="text-success font-medium">-${discount}</span>
          </div>
        )}
        <div className="flex justify-between items-center text-sm">
          <span className="text-muted-foreground">Tax (GST 18%)</span>
          <span className="text-foreground font-medium">${tax}</span>
        </div>
      </div>
      {/* Total */}
      <div className="flex justify-between items-center mb-6">
        <span className="text-lg font-heading font-bold text-foreground">Total</span>
        <span className="text-2xl font-heading font-bold text-primary">${total}</span>
      </div>
      {/* Billing Info */}
      <div className="bg-accent/10 rounded-lg p-4 space-y-2">
        <div className="flex items-start gap-2">
          <Icon name="Info" size={16} className="text-accent mt-0.5 flex-shrink-0" />
          <div className="text-sm text-foreground">
            <p className="font-medium mb-1">Next Billing Date</p>
            <p className="text-muted-foreground">
              {billingCycle === 'monthly' ? new Date(Date.now() + 30 * 24 * 60 * 60 * 1000)?.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })
                : new Date(Date.now() + 365 * 24 * 60 * 60 * 1000)?.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
            </p>
          </div>
        </div>
      </div>
      {/* Money Back Guarantee */}
      <div className="mt-4 flex items-center gap-2 text-sm text-muted-foreground">
        <Icon name="Shield" size={14} className="text-success" />
        <span>30-day money-back guarantee</span>
      </div>
    </div>
  );
};

export default OrderSummary;